import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Icon, type IconName } from '../../components/Icon'
import { LogoMark } from '../../components/ui'
import { landingTarget } from '../../lib/website'

export function Heading({
  eyebrow,
  sans,
  serif,
  align = 'left',
  dark = false,
  as: Tag = 'h2',
  id,
}: {
  eyebrow?: string
  sans: string
  serif: string
  align?: 'left' | 'center'
  dark?: boolean
  as?: 'h1' | 'h2'
  id?: string
}) {
  const big = Tag === 'h1'
  return (
    <div className={`flex flex-col gap-4 ${align === 'center' ? 'items-center text-center' : 'items-start'}`}>
      {eyebrow && <p className={`eyebrow ${dark ? '!text-white/70' : ''}`}>{eyebrow}</p>}
      <Tag id={id} className={`flex flex-col ${dark ? 'text-white' : 'text-ink'}`}>
        <span
          className={`h-sans ${
            big
              ? 'text-[42px] leading-[43px] sm:text-[60px] sm:leading-[62px] lg:text-[76px] lg:leading-[78px]'
              : 'text-[34px] leading-[36px] lg:text-[52px] lg:leading-[55px]'
          }`}
        >
          {sans}
        </span>
        <span
          className={`h-serif ${
            big
              ? 'text-[46px] leading-[47px] sm:text-[66px] sm:leading-[68px] lg:text-[84px] lg:leading-[86px]'
              : 'text-[38px] leading-[40px] lg:text-[58px] lg:leading-[61px]'
          }`}
        >
          {serif}
        </span>
      </Tag>
    </div>
  )
}

export function TrialCta({ label = 'Start free trial', size = 'lg' }: { label?: string; size?: 'lg' | 'md' }) {
  return (
    <Link to="/login" className={`btn btn-primary ${size === 'lg' ? 'btn-lg' : ''}`}>
      {label}
    </Link>
  )
}

/**
 * Website field plus "Start for free". A filled field must be a valid website (format only) and goes to
 * `/login?website=<normalised URL>`; an empty field goes straight to `/login`.
 */
export function WebsiteStart({ dark = false, className = '', variant = 'default' }: { dark?: boolean; className?: string; variant?: 'default' | 'hero' }) {
  const navigate = useNavigate()
  const id = useId()
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const target = landingTarget(value)
    if ('error' in target) {
      setError(target.error)
      return
    }
    setError(null)
    void navigate(target.to)
  }
  const input = (className: string) => (
    <input
      id={id}
      className={className}
      type="text"
      inputMode="url"
      autoComplete="url"
      autoCapitalize="none"
      spellCheck={false}
      maxLength={500}
      placeholder="yourbusiness.com"
      value={value}
      onChange={(e) => {
        setValue(e.target.value)
        if (error) setError(null)
      }}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-err` : undefined}
    />
  )
  return (
    <form
      onSubmit={submit}
      noValidate
      className={`flex w-full flex-col gap-2 ${variant === 'hero' ? 'max-w-[540px] items-center' : 'max-w-[560px]'} ${className}`}
    >
      <label htmlFor={id} className="sr-only">
        Your website
      </label>
      {variant === 'hero' ? (
        /* Pencil "Start form": one white field with the button inside on desktop; stacked on mobile. */
        <div className="flex w-full flex-col gap-2.5 sm:flex-row sm:gap-1.5 sm:rounded-[12px] sm:border sm:border-border sm:bg-surface sm:p-1.5 sm:shadow-[0_8px_24px_0_#16130F14] sm:focus-within:outline-2 sm:focus-within:outline-accent">
          <div className="flex h-12 min-w-0 items-center gap-2 rounded-[10px] border border-border bg-surface px-3.5 focus-within:outline-2 focus-within:outline-accent sm:h-auto sm:flex-1 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-3 sm:focus-within:outline-0">
            <Icon name="globe" size={16} className="shrink-0 text-faint" />
            {input('min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-faint')}
          </div>
          <button type="submit" className="btn btn-primary w-full !rounded-[9px] !px-5 !py-3 !text-[15px] sm:w-auto">
            Start for free
            <Icon name="arrow-right" size={16} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 sm:flex-row">
          {input('input min-w-0 flex-1 !py-3 !text-[15px] shadow-[0_2px_8px_0_#16130F0F]')}
          <button type="submit" className="btn btn-primary btn-lg w-full sm:w-auto">
            Start for free
            <Icon name="arrow-right" size={15} />
          </button>
        </div>
      )}
      {error && (
        <p id={`${id}-err`} role="alert" className={`text-left text-[13px] font-medium ${dark ? 'text-white' : 'text-danger'}`}>
          {error}
        </p>
      )}
    </form>
  )
}

/**
 * Product video in a browser frame ("Video player" in the hero). Shows the poster with a play button;
 * pressing it starts the video with sound off and native controls.
 */
export function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  return (
    <figure id="video" className="mt-1 w-full max-w-[1040px] scroll-mt-24 overflow-hidden rounded-[16px] bg-surface text-left shadow-[0_30px_80px_0_#16130F40]">
      <div className="flex items-center gap-1.5 border-b border-border bg-bg px-4 py-3" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="ml-3 text-[12px] text-faint">lobbyreply.com</span>
      </div>
      <div className="relative aspect-video w-full bg-sand">
        <video
          ref={ref}
          className="block h-full w-full"
          src="/video/lobby-demo.mp4"
          poster="/video/lobby-demo-poster.jpg"
          muted
          playsInline
          controls={playing}
          preload="none"
          onPlay={() => setPlaying(true)}
          aria-label="Product video: how Lobby answers messages and comments on WhatsApp, Instagram and Facebook, follows up, and how setup works"
        />
        {!playing && (
          <button
            type="button"
            className="absolute inset-0 flex items-center justify-center"
            onClick={() => void ref.current?.play().catch(() => {})}
            aria-label="Play the product video"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-ink shadow-[0_8px_24px_0_#16130F33] transition-transform hover:scale-105 lg:h-[84px] lg:w-[84px]">
              <Icon name="play" size={20} className="translate-x-[1px] lg:hidden" />
              <Icon name="play" size={30} className="hidden translate-x-[2px] lg:block" />
            </span>
          </button>
        )}
      </div>
    </figure>
  )
}

export function Section({ id, children, className = '' }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={`mx-auto w-full max-w-[1440px] scroll-mt-20 px-5 pt-[72px] sm:px-10 lg:px-[120px] lg:pt-[110px] ${className}`}>
      {children}
    </section>
  )
}

/** WhatsApp-style chat card from the hero ("WhatsApp chat card" in Lobby.pen). */
export function ChatCard({ className = '' }: { className?: string }) {
  const lines: { from: 'lead' | 'bot'; text: string; time: string }[] = [
    { from: 'lead', text: 'Hi, our roof is leaking since last night’s storm. Can someone come by this week?', time: '22:14' },
    {
      from: 'bot',
      text: 'Sorry to hear that. I’m Summit Roofing’s automated assistant. Is water coming in right now, or is it a damp patch?',
      time: '22:14 · 8 s later',
    },
    { from: 'lead', text: 'Water drips into the attic when it rains.', time: '22:15' },
    { from: 'bot', text: 'Then we’ll come quickly. Thomas can inspect tomorrow 8:30 or 11:00. Which suits you?', time: '22:15' },
    { from: 'lead', text: '8:30 please', time: '22:16' },
  ]
  return (
    <figure
      className={`overflow-hidden rounded-[18px] bg-wa shadow-[0_24px_60px_0_#16130F4D] ${className}`}
      aria-label="Example WhatsApp conversation between a customer and the assistant"
    >
      <div className="flex items-center gap-2.5 border-b border-border bg-surface px-3.5 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-white" aria-hidden>
          SR
        </span>
        <div className="flex flex-col">
          <span className="text-[13px] font-semibold">Summit Roofing</span>
          <span className="text-[11px] text-muted">online</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-3 py-3.5">
        {lines.map((l, i) => (
          <div key={i} className={`flex ${l.from === 'bot' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`flex max-w-[82%] flex-col gap-1 px-2.5 py-2 ${
                l.from === 'bot' ? 'rounded-[10px_2px_10px_10px] bg-bubble-assistant' : 'rounded-[2px_10px_10px_10px] bg-surface'
              }`}
            >
              <p className="text-[12.5px] leading-[18px]">{l.text}</p>
              <span className="self-end text-[10.5px] text-muted">{l.time}</span>
            </div>
          </div>
        ))}
      </div>
      <figcaption className="flex items-center gap-2 bg-accent px-3.5 py-2.5 text-[12px] font-semibold text-white">
        <Icon name="calendar-check" size={14} />
        Booked: inspection tomorrow 8:30, Thomas
      </figcaption>
    </figure>
  )
}

const NAV_MOCK: { icon: IconName; label: string; active?: boolean }[] = [
  { icon: 'layout-dashboard', label: 'Today', active: true },
  { icon: 'message-square', label: 'Conversations' },
  { icon: 'repeat', label: 'Jobs' },
  { icon: 'building-2', label: 'Business profile' },
  { icon: 'book-open', label: 'Training' },
  { icon: 'messages-square', label: 'Channels' },
]

/** Browser window with a miniature dashboard. Decorative; the real one lives at /app. */
export function AppWindowMock({ url, children, activeLabel = 'Today' }: { url: string; children: ReactNode; activeLabel?: string }) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-[16px] bg-surface shadow-[0_30px_80px_0_#16130F40]" aria-hidden>
      <div className="flex items-center gap-1.5 border-b border-border bg-bg px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="ml-3 text-[12px] text-faint">{url}</span>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="hidden w-[200px] shrink-0 flex-col gap-1 border-r border-border bg-bg px-3 py-[18px] md:flex">
          <div className="mb-3.5 flex items-center gap-2">
            <LogoMark />
            <span className="font-head text-[19px] font-bold tracking-[-0.4px]">Lobby</span>
          </div>
          {NAV_MOCK.map((n) => (
            <div
              key={n.label}
              className={`flex items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-[13px] ${
                n.label === activeLabel ? 'bg-sand font-semibold text-ink' : 'font-medium text-muted'
              }`}
            >
              <Icon name={n.icon} size={14} />
              {n.label}
            </div>
          ))}
        </div>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  )
}

export function ProfileMock() {
  const rows: { l: string; v: string; ok: boolean }[] = [
    { l: 'Business name', v: 'Summit Roofing Ltd', ok: true },
    { l: 'Website', v: 'https://summitroofing.co', ok: true },
    { l: 'Industry', v: 'Roofing', ok: true },
    { l: 'Opening hours', v: 'Mon–Fri 8:00–18:00, Thu until 20:00', ok: true },
    {
      l: 'What you offer',
      v: 'Roof repairs, storm damage, flat roofs, gutters, skylights. Free inspection within 48 hours.',
      ok: false,
    },
    { l: 'Prices it may name', v: 'Inspection free. Never quote repairs before an inspection.', ok: false },
    { l: 'Who takes over', v: 'Office (Anna) for bookings · Thomas for technical questions', ok: false },
  ]
  return (
    <div className="flex h-full flex-col gap-4 px-[30px] py-[26px]">
      <p className="font-head text-[22px] font-semibold tracking-[-0.4px]">Business profile</p>
      <div className="flex items-center gap-2 rounded-[10px] border border-border bg-bg px-3 py-2.5 text-[12.5px] font-medium">
        <Icon name="sparkles" size={14} className="text-accent" />
        Drafted from summitroofing.co. The assistant only uses lines you have confirmed. 4 of 7 confirmed
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.l} className={`flex flex-col gap-1.5 ${r.v.length > 40 ? 'sm:col-span-2' : ''}`}>
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold">{r.l}</span>
              <span className={`chip !px-2 !py-0.5 !text-[10.5px] ${r.ok ? 'bg-sand text-muted' : 'bg-accent-soft text-accent'}`}>
                <Icon name={r.ok ? 'check' : 'sparkles'} size={11} />
                {r.ok ? 'Confirmed' : 'AI draft · review'}
              </span>
            </div>
            <div className="rounded-[9px] border border-border px-3 py-2 text-[12.5px] leading-[18px]">{r.v}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
